<template>
  <div class="py-12">
    <!-- Pré-charger les utilisateurs restaurant pour le propriétaire -->
    <!-- <div class="hidden">
      <DataFetcher dataKey="restaurantUsers" v-slot="{ loading: usersLoading }">
      </DataFetcher>
    </div> -->

    <DataFetcher
      dataKey="restaurant"
      :filters="{ companyId: restaurant?.id }"
      v-slot="{ loading: restaurantDataLoading, data: loadedRestaurantData }"
    >
      <div
        class="space-y-6"
        :class="{ shimmerContainer: restaurantDataLoading }"
      >
        <div class="space-y-6">
          <!-- Header : logo, nom et status -->
          <div
            class="rounded-2xl border border-gray-200 bg-white p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          >
            <div class="flex items-center gap-4">
              <div
                class="shimmerTarget w-20 h-20 rounded-full overflow-hidden border"
              >
                <ImgComponent
                  :src="loadedRestaurantData?.logo"
                  fallback="/images/logo.png"
                  alt="Logo"
                  class="w-full h-full object-cover"
                />
              </div>
              <div>
                <h1 class="text-3xl font-bold title-color shimmerTarget">
                  {{ loadedRestaurantData?.name }}
                </h1>
                <div
                  class="text-sm font-medium px-3 py-1 rounded-full inline-block mt-1 shimmerTarget"
                  :class="
                    loadedRestaurantData?.isVisible
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  "
                >
                  {{ loadedRestaurantData?.isVisible ? 'Ouvert' : 'Fermé' }}
                </div>
                <div class="text-sm text-gray-600 mt-2 shimmerTarget">
                  Propriétaire :
                  <span class="font-semibold">{{
                    loadedRestaurantData?.owner || '-'
                  }}</span>
                </div>
              </div>
            </div>

            <div class="flex items-center gap-3">
              <span class="text-sm font-medium text-gray-600">Statut :</span>
              <select
                class="border border-gray-300 text-sm rounded-lg focus:ring-orange-500 focus:border-orange-500 block p-2 outline-none shimmerTarget bg-white"
                :value="loadedRestaurantData?.status"
                @change="updateStatus($event.target.value)"
              >
                <option value="draft">Brouillon</option>
                <option value="submitted">Soumis</option>
                <option value="under_review">En Revue</option>
                <option value="rejected">Rejeté</option>
                <option value="approved">Approuvé</option>
              </select>
            </div>
          </div>

          <!-- Soldes -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4" v-if="false">
            <!-- Solde disponible -->
            <div
              class="rounded-2xl border border-gray-200 bg-white p-6 text-center flex flex-col items-center"
            >
              <DataFetcher
                dataKey="restaurantBalances"
                :filters="{ storeId: restaurant?.id || restaurant?.userId }"
                v-slot="{ data, loading }"
              >
                <div class="text-sm text-gray-500 shimmerTarget">
                  Solde Disponible
                </div>
                <div class="text-2xl font-bold text-green-600 shimmerTarget">
                  {{
                    loading
                      ? '...'
                      : `${Commons.formatNumber(data?.availableBalance || 0, { suffix: ' FCFA' })}`
                  }}
                </div>
              </DataFetcher>
            </div>

            <!-- Solde en attente -->
            <div
              class="rounded-2xl border border-gray-200 bg-white p-6 text-center flex flex-col items-center"
            >
              <DataFetcher
                dataKey="restaurantBalances"
                :filters="{ storeId: restaurant?.id || restaurant?.userId }"
                v-slot="{ data, loading }"
              >
                <div class="text-sm text-gray-500 shimmerTarget">
                  Solde en Attente
                </div>
                <div class="text-2xl font-bold text-orange-600 shimmerTarget">
                  {{
                    loading
                      ? '...'
                      : `${Commons.formatNumber(data?.pendingBalance || 0, { suffix: ' FCFA' })}`
                  }}
                </div>
                <div class="text-xs text-gray-400 mt-1 shimmerTarget">
                  {{ data?.pendingOrderCount || 0 }} commande(s) non
                  récupérée(s)
                </div>
              </DataFetcher>
            </div>
          </div>

          <!-- Localisation -->
          <div class="rounded-2xl border border-gray-200 bg-white p-6">
            <h3 class="text-lg font-semibold text-gray-800 shimmerTarget">
              Localisation
            </h3>
            <p class="mb-1 shimmerTarget">
              {{ loadedRestaurantData?.address }},
              {{ loadedRestaurantData?.city }}
            </p>
            <div
              v-if="restaurant.latitude && loadedRestaurantData?.longitude"
              class="mt-2 shimmerTarget"
            >
              <iframe
                class="w-full h-64 rounded-2xl border"
                :src="`https://www.google.com/maps?q=${restaurant.latitude},${restaurant.longitude}&hl=fr&z=15&output=embed`"
              ></iframe>
            </div>
          </div>

          <!-- Horaires -->
          <div class="rounded-2xl border border-gray-200 bg-white p-6">
            <h3 class="text-lg font-semibold text-gray-800 shimmerTarget">
              Horaires d'ouverture
            </h3>
            <ul class="space-y-2 shimmerTarget">
              <li
                v-for="(hour, day) in loadedRestaurantData?.openingHours"
                :key="day"
                class="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
              >
                <span class="font-medium text-gray-700 capitalize">{{
                  day
                }}</span>
                <span class="text-gray-600 text-sm">
                  <span v-if="hour.opened">
                    <span v-if="hour.lunch">{{ hour.lunch }}</span>
                    <span v-if="hour.lunch && hour.dinner"> | </span>
                    <span v-if="hour.dinner">{{ hour.dinner }}</span>
                  </span>
                  <span v-else class="text-red-500">Fermé</span>
                </span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Menu Management Tabs -->
        <div
          class="rounded-2xl border border-gray-200 bg-white overflow-hidden"
        >
          <div
            class="flex border-b border-gray-100 bg-gray-50/50 overflow-x-auto no-scrollbar"
          >
            <button
              @click="activeMenuTab = 'dishes'"
              class="px-6 py-4 text-sm font-bold transition-all border-b-2 whitespace-nowrap"
              :class="
                activeMenuTab === 'dishes'
                  ? 'border-orange-500 text-orange-600 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              "
            >
              Plats ({{ dishesCount }})
            </button>
            <button
              @click="activeMenuTab = 'drinks'"
              class="px-6 py-4 text-sm font-bold transition-all border-b-2 whitespace-nowrap"
              :class="
                activeMenuTab === 'drinks'
                  ? 'border-orange-500 text-orange-600 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              "
            >
              Boissons ({{ (loadedRestaurantData?.drinks || []).length }})
            </button>
            <button
              @click="activeMenuTab = 'accompaniments'"
              class="px-6 py-4 text-sm font-bold transition-all border-b-2 whitespace-nowrap"
              :class="
                activeMenuTab === 'accompaniments'
                  ? 'border-orange-500 text-orange-600 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              "
            >
              Accompagnements
            </button>
          </div>

          <div class="p-6">
            <!-- Dishes Tab -->
            <div v-if="activeMenuTab === 'dishes'" class="space-y-6">
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-bold text-gray-800">Plats proposés</h3>
                <button
                  @click="openDishModal()"
                  class="px-4 py-2 bg-orange-600 text-white rounded-xl text-sm font-bold hover:bg-orange-700 shadow-sm transition-all flex items-center gap-2"
                >
                  <span>+ Ajouter un plat</span>
                </button>
              </div>

              <DataFetcher
                dataKey="dishes"
                :filters="{
                  storeId:
                    loadedRestaurantData?.id || loadedRestaurantData?.userId,
                }"
                @fetched="onDishesFetched"
                v-slot="{ loading, data, refresh }"
              >
                <div
                  v-if="loading"
                  class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                  <div
                    v-for="i in 3"
                    :key="i"
                    class="h-64 bg-gray-50 animate-pulse rounded-2xl"
                  ></div>
                </div>
                <div
                  v-else-if="!data?.result?.length"
                  class="py-12 flex flex-col items-center justify-center text-gray-400 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-100"
                >
                  <svg
                    class="w-12 h-12 mb-3 opacity-20"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  <p class="text-sm">
                    Aucun plat enregistré pour ce restaurant
                  </p>
                </div>
                <div
                  v-else
                  class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                  <div
                    v-for="dish in data.result"
                    :key="dish.id"
                    @click="openDishModal(dish)"
                    class="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition cursor-pointer group"
                  >
                    <div class="relative h-40 overflow-hidden">
                      <ImgComponent
                        :src="dish.principalImageUrl"
                        fallback="/images/logo.png"
                        class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div
                        class="absolute top-3 right-3 px-2 py-1 bg-white/90 backdrop-blur rounded-lg text-xs font-bold shadow-sm"
                      >
                        {{ dish.price }} {{ dish.currency || 'XOF' }}
                      </div>
                    </div>
                    <div class="p-4">
                      <div class="flex items-start justify-between gap-2">
                        <h4 class="font-bold text-gray-900 leading-tight">
                          {{ dish.title }}
                        </h4>
                        <div
                          v-if="!dish.active"
                          class="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px] font-bold uppercase"
                        >
                          Inactif
                        </div>
                      </div>
                      <p class="text-xs text-gray-500 mt-1 line-clamp-2 h-8">
                        {{ dish.description || 'Aucune description' }}
                      </p>
                      <div class="mt-3 flex items-center justify-between">
                        <div
                          class="text-[10px] text-orange-600 font-bold bg-orange-50 px-2 py-0.5 rounded-md uppercase tracking-wider"
                        >
                          {{ dish.category || 'Sans catégorie' }}
                        </div>
                        <div class="text-[10px] text-gray-400 font-medium">
                          {{ dish.cookDurationInMin }} min
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Refresh trigger after modal success -->
                <div class="hidden">{{ setRefreshDishes(refresh) }}</div>
              </DataFetcher>
            </div>

            <!-- Drinks Tab -->
            <div v-if="activeMenuTab === 'drinks'" class="space-y-6">
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-bold text-gray-800">Boissons</h3>
                <button
                  @click="openDrinkModal()"
                  class="px-4 py-2 bg-orange-600 text-white rounded-xl text-sm font-bold hover:bg-orange-700 shadow-sm transition-all flex items-center gap-2"
                >
                  <span>+ Ajouter une boisson</span>
                </button>
              </div>

              <div
                v-if="!loadedRestaurantData?.drinks?.length"
                class="py-12 flex flex-col items-center justify-center text-gray-400 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-100"
              >
                <svg
                  class="w-12 h-12 mb-3 opacity-20"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                <p class="text-sm">Aucune boisson enregistrée</p>
              </div>
              <div
                v-else
                class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
              >
                <div
                  v-for="drink in loadedRestaurantData.drinks"
                  :key="drink.id"
                  @click="openDrinkModal(drink)"
                  class="bg-white border border-gray-100 rounded-2xl p-3 shadow-sm hover:shadow-md transition cursor-pointer flex flex-col items-center text-center group"
                >
                  <div
                    class="w-20 h-20 mb-3 group-hover:scale-110 transition-transform"
                  >
                    <ImgComponent
                      :src="drink.image"
                      fallback="/images/logo.png"
                      class="w-full h-full object-contain"
                    />
                  </div>
                  <div class="text-sm font-bold text-gray-900 line-clamp-1 h-5">
                    {{ drink.name }}
                  </div>
                  <div class="text-xs font-bold text-orange-600 mt-1">
                    {{ drink.amount }} FCFA
                  </div>
                  <div class="text-[10px] text-gray-400 mt-1">
                    Max: {{ drink.max }}
                  </div>

                  <button
                    @click.stop="
                      confirmDeleteDrink(drink, loadedRestaurantData)
                    "
                    class="mt-3 p-1.5 text-rose-300 hover:text-rose-500 transition-colors"
                  >
                    <svg
                      width="14"
                      height="14"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Accompaniments Tab -->
            <div v-if="activeMenuTab === 'accompaniments'" class="space-y-6">
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-lg font-bold text-gray-800">
                    Accompagnements
                  </h3>
                  <p class="text-xs text-gray-500 mt-0.5">
                    Personnalisez les prix pour ce restaurant
                  </p>
                </div>
                <div class="flex gap-2">
                  <button
                    @click="openGlobalAccompaniementModal()"
                    class="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-xl text-xs font-bold hover:bg-gray-50 transition-all flex items-center gap-2"
                  >
                    <span>Gérer la liste globale</span>
                  </button>
                </div>
              </div>

              <DataFetcher
                dataKey="accompaniements"
                v-slot="{ loading, data: accData }"
              >
                <div
                  v-if="loading"
                  class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                >
                  <div
                    v-for="i in 4"
                    :key="i"
                    class="h-20 bg-gray-50 animate-pulse rounded-2xl"
                  ></div>
                </div>
                <div
                  v-else
                  class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                >
                  <div
                    v-for="acc in accData?.result"
                    :key="acc.id"
                    class="bg-white border border-gray-100 rounded-2xl p-4 flex items-center gap-3 relative group"
                  >
                    <div class="w-10 h-10 flex-shrink-0">
                      <ImgComponent
                        :src="acc.image"
                        class="w-full h-full object-contain"
                        fallback="/images/logo.png"
                      />
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="text-xs font-bold text-gray-900 truncate">
                        {{ acc.name }}
                      </div>
                      <div class="flex items-center gap-1 mt-1">
                        <span class="text-[10px] text-gray-400">Prix :</span>
                        <input
                          type="number"
                          :value="
                            loadedRestaurantData?.accompanimentPrice?.[
                              acc.id
                            ] || acc.amount
                          "
                          @change="
                            handleAccompPriceChange(
                              acc.id,
                              $event,
                              loadedRestaurantData,
                            )
                          "
                          class="w-16 h-6 border-b border-gray-200 text-xs font-bold text-orange-600 outline-none focus:border-orange-500 bg-transparent"
                        />
                        <span class="text-[9px] text-gray-400">XOF</span>
                      </div>
                    </div>
                  </div>
                </div>
              </DataFetcher>
            </div>
          </div>
        </div>

        <!-- Statistiques des commandes -->
        <DataFetcher
          dataKey="platformStats"
          v-slot="{ data, loading, error, refresh }"
        >
          <div v-if="loading" class="text-center py-10">
            Chargement des statistiques...
          </div>
          <div v-else-if="error" class="text-center text-red-500 py-10">
            Erreur lors du chargement des statistiques.
            <button @click="refresh">Réessayer</button>
          </div>
          <div v-else class="">
            <OperationsChart
              :filters="{
                restaurantId: loadedRestaurantData?.id,
              }"
              :statsData="data?.operations"
            />
          </div>
        </DataFetcher>

        <!-- Promotions du restaurant -->
        <DataFetcher
          dataKey="promotions"
          :filters="{
            storeId: loadedRestaurantData?.id || loadedRestaurantData?.userId,
          }"
        >
          <template #any="{ data: promotions, loading, error, refresh }">
            <div v-if="loading" class="text-center py-10">
              Chargement des promotions...
            </div>
            <div v-else-if="error" class="text-center text-red-500 py-10">
              Erreur lors du chargement des promotions.
              <button @click="refresh">Réessayer</button>
            </div>
            <div
              v-else-if="promotions && promotions.result?.length > 0"
              class="rounded-2xl border border-gray-200 bg-white p-6"
            >
              <h3 class="text-lg font-semibold text-gray-800 mb-4">
                Promotions en cours
              </h3>
              <div class="space-y-4">
                <div
                  v-for="promo in promotions.result"
                  :key="promo.id"
                  class="bg-gray-50 rounded-xl p-4"
                >
                  <div class="flex items-center justify-between mb-3">
                    <div>
                      <span class="text-sm text-gray-500"
                        >Coût de mise en avant</span
                      >
                      <div class="font-bold text-primary-600">
                        {{
                          promo.type === 'percentage'
                            ? `${promo.cost}%`
                            : Commons.formatNumber(promo.cost, {
                                suffix: ' FCFA',
                              })
                        }}
                      </div>
                    </div>
                    <div class="text-right">
                      <span class="text-sm text-gray-500">Période</span>
                      <div class="text-sm font-medium">
                        {{ Commons.time(promo.startDate) }} -
                        {{ Commons.time(promo.endDate) }}
                      </div>
                    </div>
                  </div>

                  <!-- Produits concernés -->
                  <DataFetcher
                    v-if="
                      promo.targetType === 'dish' &&
                      promo.productsId?.length > 0
                    "
                    dataKey="dishes"
                    v-slot="{ data: allDishesData }"
                  >
                    <div
                      v-if="
                        allDishesData?.result &&
                        promo.productsId &&
                        promo.productsId.length > 0
                      "
                      class="space-y-2"
                    >
                      <div class="text-sm text-gray-600 font-medium">
                        Produits mis en avant :
                      </div>
                      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div
                          v-for="productId in promo.productsId"
                          :key="productId"
                          class="bg-white rounded-lg p-2 flex items-center gap-3"
                        >
                          <ImgComponent
                            :src="
                              allDishesData.result.find(d => d.id === productId)
                                ?.principalImageUrl
                            "
                            fallback="/images/logo.png"
                            class="w-12 h-12 rounded-lg object-cover"
                          />
                          <div class="flex-1 min-w-0">
                            <div
                              class="font-medium text-gray-800 text-sm truncate"
                            >
                              {{
                                allDishesData.result.find(
                                  d => d.id === productId,
                                )?.title || 'Produit inconnu'
                              }}
                            </div>
                            <div class="text-xs text-gray-500">
                              {{
                                allDishesData.result.find(
                                  d => d.id === productId,
                                )?.price || 0
                              }}
                              {{
                                allDishesData.result.find(
                                  d => d.id === productId,
                                )?.currency || 'FCFA'
                              }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </DataFetcher>

                  <!-- Catégories concernées -->
                  <div
                    v-if="
                      promo.targetType === 'category' &&
                      promo.categoryIds?.length > 0
                    "
                    class="space-y-2"
                  >
                    <div class="text-sm text-gray-600 font-medium">
                      Catégories concernées :
                    </div>
                    <div class="flex flex-wrap gap-2">
                      <span
                        v-for="catId in promo.categoryIds"
                        :key="catId"
                        class="px-3 py-1 bg-blue-50 text-blue-600 rounded-lg text-xs font-semibold border border-blue-100"
                      >
                        {{ catId }}
                      </span>
                    </div>
                  </div>

                  <!-- Livraison -->
                  <div
                    v-if="promo.targetType === 'delivery'"
                    class="flex items-center gap-2 text-sm text-green-600 font-medium"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Remise sur les frais de livraison
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else
              class="rounded-2xl border border-gray-200 bg-white p-6 text-center"
            >
              <p class="text-gray-500 text-sm">Aucune promotion disponible</p>
            </div>
          </template>
        </DataFetcher>

        <!-- Coupons du restaurant -->
        <DataFetcher
          dataKey="coupons"
          :filters="{
            storeId: loadedRestaurantData?.id || loadedRestaurantData?.userId,
          }"
        >
          <template #any="{ data: coupons, loading, error, refresh }">
            <div v-if="loading" class="text-center py-10">
              Chargement des coupons...
            </div>
            <div v-else-if="error" class="text-center text-red-500 py-10">
              Erreur lors du chargement des coupons.
              <button @click="refresh">Réessayer</button>
            </div>
            <div
              v-else-if="coupons && coupons?.result?.length > 0"
              class="rounded-2xl border border-gray-200 bg-white p-6"
            >
              <h3 class="text-lg font-semibold text-gray-800 mb-4">
                Coupons attribués
              </h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div
                  v-for="coupon in coupons.result"
                  :key="coupon.id"
                  class="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200"
                >
                  <div class="flex items-center justify-between mb-2">
                    <span class="font-bold text-purple-800 text-lg">{{
                      coupon.code
                    }}</span>
                    <span
                      class="px-2 py-1 bg-purple-200 text-purple-800 rounded text-xs font-medium"
                    >
                      -{{ coupon.discount
                      }}{{ coupon.type === 'percentage' ? '%' : ' FCFA' }}
                    </span>
                  </div>
                  <div class="text-sm text-gray-700 mb-1">
                    {{ coupon.title }}
                  </div>
                  <div class="text-xs text-gray-500 mb-2">
                    {{ coupon.description }}
                  </div>
                  <div class="flex items-center justify-between text-xs">
                    <span class="text-gray-500">
                      Validité: {{ Commons.time(coupon.startDate) }} -
                      {{ Commons.time(coupon.endDate) }}
                    </span>
                    <span class="text-purple-600 font-medium"
                      >{{ coupon.nbAppliance || 0 }} utilisé(s)</span
                    >
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else
              class="rounded-2xl border border-gray-200 bg-white p-6 text-center"
            >
              <p class="text-gray-500 text-sm">Aucun coupon disponible</p>
            </div>
          </template>
        </DataFetcher>
      </div>

      <!-- Modals & Triggers -->
      <transition name="fade">
        <DishFormModal
          v-if="showDishModal"
          :restaurantId="loadedRestaurantData?.id"
          :restaurantName="loadedRestaurantData?.name"
          :initialData="selectedDish"
          @close="showDishModal = false"
          @success="onDishSaved"
        />
      </transition>

      <transition name="fade">
        <DrinkFormModal
          v-if="showDrinkModal"
          :restaurantId="loadedRestaurantData?.id"
          :currentDrinks="loadedRestaurantData?.drinks || []"
          :drinkToEdit="selectedDrink"
          @close="showDrinkModal = false"
          @success="onDrinkSaved"
        />
      </transition>

      <TableList
        v-if="showGlobalAccompModal"
        :title="'Accompagnements Globaux'"
        :dataKey="'accompaniements'"
        :cols="accompCols"
        @close="showGlobalAccompModal = false"
        class="fixed inset-0 z-[60]"
      />

      <ActionTrigger
        ref="updatePrefsTriggerRef"
        dataKey="updateStorePreferences"
        @success="onPrefsSuccess"
      />

      <ActionTrigger
        ref="updateRestaurantTriggerRef"
        dataKey="updateRestaurant"
        @success="onStatusUpdated"
      />
    </DataFetcher>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import DataFetcher from '@/features/data/presentation/components/DataFetcher.vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import ImgComponent from '@/presentation/components/img/ImgComponent.vue'
// import RestaurantOrdersChart from '../charts/RestaurantOrdersChart.vue'
import OperationsChart from '../charts/OperationsChart.vue'
import TableList from '../others/TableList.vue'
import Commons from '@/shared/utils/commons'
import { EVENTS } from '@/shared/constants/commons'
import Events from '@/events/events'

// New Modals
import DishFormModal from '../dishes/DishFormModal.vue'
import DrinkFormModal from '../dishes/DrinkFormModal.vue'

const props = defineProps({
  restaurant: {
    type: Object,
    default: null,
  },
})

// UI State
const activeMenuTab = ref('dishes')
const dishesCount = ref(0)
const refreshDishes = ref(() => {})

// Modal State
const showDishModal = ref(false)
const selectedDish = ref(undefined)
const showDrinkModal = ref(false)
const selectedDrink = ref(undefined)
const showGlobalAccompModal = ref(false)

const accompCols = [
  { name: 'Nom', value: el => [el.name], id: 'name' },
  { name: 'Prix de base', value: el => [`${el.amount} FCFA`], id: 'amount' },
]

// Actions
const setRefreshDishes = fn => {
  refreshDishes.value = fn
}

const onDishesFetched = data => {
  dishesCount.value = data?.data?.total || 0
}

const openDishModal = (dish = undefined) => {
  selectedDish.value = dish
  showDishModal.value = true
}

const onDishSaved = () => {
  showDishModal.value = false
  refreshDishes.value()
}

const openDrinkModal = (drink = undefined) => {
  selectedDrink.value = drink
  showDrinkModal.value = true
}

const onDrinkSaved = () => {
  showDrinkModal.value = false
  // Refresh restaurant data to see new drinks
  Events.dispatch(EVENTS.APPLICATION_WORK_FINISHED)
}

const confirmDeleteDrink = (drink, restaurantData) => {
  if (!confirm(`Supprimer la boisson ${drink.name} ?`)) return

  const updatedDrinks = (restaurantData.drinks || []).filter(
    d => d.id !== drink.id,
  )

  // Create a temporary hidden trigger or use a shared service
  // For now, let's trigger the update via Events to reuse existing patterns if any
  Events.dispatch(EVENTS.START_APPLICATION_WORK, { title: 'Suppression...' })

  // We'll use the updateStorePreferences trigger via a ref in the template
  updatePrefsTriggerRef.value?.trigger({
    restaurantId: restaurantData.id,
    drinks: updatedDrinks,
  })
}

const updatePrefsTriggerRef = ref(null)

const onPrefsSuccess = () => {
  Events.dispatch(EVENTS.APPLICATION_WORK_FINISHED)
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: 'Menu mis à jour',
    type: 'success',
  })
}

const updateRestaurantTriggerRef = ref(null)

const updateStatus = newStatus => {
  if (!newStatus || !props.restaurant?.id) return

  Events.dispatch(EVENTS.START_APPLICATION_WORK, {
    title: 'Mise à jour du statut...',
  })
  updateRestaurantTriggerRef.value?.trigger({
    restaurantId: props.restaurant.id,
    status: newStatus,
  })
}

const onStatusUpdated = () => {
  Events.dispatch(EVENTS.APPLICATION_WORK_FINISHED)
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: 'Statut du restaurant mis à jour',
    type: 'success',
  })
  // Force a refresh of the restaurant data
  // The DataFetcher will reload if we use its refresh, but here we can just do a global event or let it be
}

/**
 * @param {string} id
 * @param {any} event
 * @param {any} restaurantData
 */
const handleAccompPriceChange = (id, event, restaurantData) => {
  const target = event.target
  if (target) {
    updateAccompaniementPrice(id, target.value, restaurantData)
  }
}

/**
 * @param {string} id
 * @param {string|number} price
 * @param {any} restaurantData
 */
const updateAccompaniementPrice = (id, price, restaurantData) => {
  if (!restaurantData) return
  const prices = { ...(restaurantData.accompanimentPrice || {}) }
  prices[id] = Number(price)

  updatePrefsTriggerRef.value?.trigger({
    restaurantId: restaurantData.id,
    accompanimentPrice: prices,
  })
}

const openGlobalAccompaniementModal = () => {
  showGlobalAccompModal.value = true
}
</script>

<style scoped>
.title-color {
  color: #1f2937;
}
</style>
